import { useEffect, useRef } from 'react';

type DebouncedFn = ((...args: any[]) => void) & { cancel: () => void };

const useDebounce = (fn: (...args: any[]) => void, delay: number): DebouncedFn => {
  const timeoutId = useRef<NodeJS.Timeout | null>(null);

  const cancel = (): void => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
      timeoutId.current = null;
    }
  };

  // The pending timer must not outlive the component: a late callback writing the
  // URL replaces the route the user has already left. A value whose only path
  // home is the debounce commits from an explicit boundary (blur, Enter, Apply)
  // and calls `cancel()` there — never from this teardown, which cannot tell a
  // drawer closing from a route unmounting.
  useEffect(() => cancel, []);

  const debounced = (...args: any[]): void => {
    cancel();

    timeoutId.current = setTimeout(() => {
      timeoutId.current = null;
      fn(...args);
    }, delay);
  };

  debounced.cancel = cancel;

  return debounced;
};

export default useDebounce;
